import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import ResourcesBanner from './ResourcesBanner';
import { fetchTechResources } from '../../lib/contentful';

const ResourcesPage = () => {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    async function load() {
      setLoading(true);
      try {
        const entries = await fetchTechResources().catch((e) => {
          console.warn('Contentful fetch failed:', e.message);
          return null;
        });

        if (!mounted) return;
        if (entries && entries.length) {
          setResources(entries);
        } else {
          setResources([]);
        }
      } catch (e) {
        console.error(e);
        setError('Failed to load resources');
        if (mounted) setResources([]);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, []);

  const dynamicCategories = useMemo(() => {
    const uniqueCats = new Set();
    resources.forEach(res => {
      if (Array.isArray(res.category)) {
        res.category.forEach(c => uniqueCats.add(c));
      } else if (res.category && typeof res.category === 'string') {
        uniqueCats.add(res.category);
      }
    });
    
    const cats = Array.from(uniqueCats).map(c => ({
      id: c,
      name: c.charAt(0).toUpperCase() + c.slice(1),
      description: `${c.charAt(0).toUpperCase() + c.slice(1)} Resources`
    }));

    cats.sort((a, b) => a.name.localeCompare(b.name));

    return [
      { id: "all", name: "All", description: "All Design Engineering Resources" },
      ...cats
    ];
  }, [resources]);

  const handleCategoryClick = (categoryId) => {
    setSelectedCategory(categoryId === selectedCategory ? "all" : categoryId);
  };

  const matchesCategory = (item) => {
    if (selectedCategory === 'all' || !selectedCategory) return true;
    if (Array.isArray(item.category) && item.category.length) {
      return item.category.includes(selectedCategory);
    }
    return [item.type, item.brand, item.slug, item.id].includes(selectedCategory);
  };

  const filteredItems = resources.filter(matchesCategory);

  return (
    <div className="page pt-20">
      <Navbar />
      <ResourcesBanner />
        <div className="px-10 py-12">
           <div>
            <div className="flex flex-wrap gap-3">
                {dynamicCategories.map((category) => {
                  const count = resources.reduce((acc, it) => {
                    if (category.id === 'all') return resources.length;
                    if (Array.isArray(it.category) && it.category.includes(category.id)) return acc + 1;
                    return acc;
                  }, 0);

                  return (
                    <button
                      key={category.id}
                      onClick={() => handleCategoryClick(category.id)}
                      className={`rounded-full border-neutral-600 px-4 py-2 border text-sm font-medium cursor-pointer w-fit transition-all ${
                        selectedCategory === category.id
                          ? 'bg-neutral-800 text-[#e3eed1cc] border-[#e3eed1cc] '
                          : 'text-[#ECF2E1] bg-none hover:bg-neutral-800 hover:text-white'
                      }`}
                      title={category.description}
                    >
                      {category.name} {count > 0 && category.id !== 'all' ? `(${count})` : ''}
                      {category.id === 'all' ? `(${resources.length})` : ''}
                    </button>
                  )
                })}
            </div>
            
           </div> 
           <div className="mt-5">
            <p className="text-neutral-400 font-medium text-sm">{selectedCategory ? (dynamicCategories.find(cat => cat.id === selectedCategory)?.description ?? 'All') : 'All'}</p>
            <div className="mt-10">
              {loading && (
                <div className="py-10 px-10 pt-0">
                  <div className="flex items-center justify-center h-48">
                    <svg className="w-8 h-8 text-neutral-500 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                    </svg>
                    <span className="ml-3 text-neutral-500">Loading resources...</span>
                  </div>
                </div>
              )}
              {error && <p className="text-sm text-red-400">{error}</p>}
              
              {(!loading && filteredItems.length === 0) ? (
                <div className="py-12 text-left col-span-full">
                  <p className="text-neutral-400 font-medium text-lg">Check back soon</p>
                  <p className="text-neutral-500 text-sm mt-2">There are no items in the <span className="font-semibold">{selectedCategory === 'all' ? 'resources' : (dynamicCategories.find(c=>c.id===selectedCategory)?.name || selectedCategory)}</span> category right now.</p>
                </div>
              ) : (
                !loading && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredItems.map((resource, idx) => (
                      <Link 
                        key={resource.id || idx} 
                        to={`/resources/${resource.id}`}
                        className="block p-6 rounded-2xl border border-neutral-800 bg-neutral-900/50 hover:bg-neutral-800 transition-colors group"
                      >
                        <div className="flex justify-between items-start mb-4">
                          <span className="text-xs font-semibold uppercase tracking-wider text-neutral-500 bg-neutral-800 px-2 py-1 rounded">
                            {resource.category && resource.category.length > 0 ? resource.category[0] : 'General'}
                          </span>
                          <svg className="w-5 h-5 text-neutral-500 group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                          </svg>
                        </div>
                        <h3 className="text-xl font-medium text-white mb-2">{resource.title}</h3>
                        <p className="text-neutral-400 text-sm">{resource.description}</p>
                      </Link>
                    ))}
                  </div>
                )
              )}
            </div>
           </div>
        </div>
      <Footer/>
    </div>
  )
}

export default ResourcesPage;
